import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Divider } from 'antd';

import { TABLE_PAGE_SIZE_OPTIONS } from 'constants/stars';
import { isUserAuthorized } from 'utils/helpers';
import { ACL_MATRIX } from 'common/permissions';
import { exportFreshDeskTickets, getFreshDeskTickets } from 'services/business';
import { TICKET_STATUS_TAG } from 'constants/business-details';

import BRTable from 'components/BRTable/BRTable';
import BRTag from 'components/BRTag/BRTag';
import { notify } from 'components/Notify/Notify';
import StatsTags from './components/StatsTags/StatsTags';

import { ReactComponent as FreshDeskIcon } from 'assets/bosta-icons/life-buoy.svg';

import './FreshDeskTable.less';

const FreshDeskTable = ({ businessId, date, ...props }) => {
  const intl = useIntl();
  const refreshTable = useRef();

  const [ticketsData, setTicketsData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const acceptMethods = (refreshMethod) => {
    refreshTable.current = refreshMethod;
  };

  useEffect(() => {
    if (refreshTable.current) {
      refreshTable.current();
    }
  }, [date]);

  const starsTableColumns = [
    {
      key: 'ticketId',
      dataIndex: 'ticketId',
      width: 200,
      title: intl.formatMessage({
        id: 'merchant_analysis.freshdesk_table.table_columns.ticket_id'
      }),
      fixed: 'left'
    },
    {
      key: 'status',
      dataIndex: 'status',
      width: 200,
      title: intl.formatMessage({
        id: 'merchant_analysis.freshdesk_table.table_columns.status'
      })
    },
    {
      key: 'category',
      dataIndex: 'category',
      width: 200,
      title: intl.formatMessage({
        id: 'merchant_analysis.freshdesk_table.table_columns.category'
      })
    },
    {
      key: 'sub_category',
      dataIndex: 'sub_category',
      width: 200,
      title: intl.formatMessage({
        id: 'merchant_analysis.freshdesk_table.table_columns.sub_category'
      })
    },
    {
      key: 'investigation_result',
      dataIndex: 'investigation_result',
      width: 200,
      title: intl.formatMessage({
        id: 'merchant_analysis.freshdesk_table.table_columns.investigation_result'
      })
    },
    {
      key: 'rejection_reason',
      dataIndex: 'rejection_reason',
      width: 200,
      title: intl.formatMessage({
        id: 'merchant_analysis.freshdesk_table.table_columns.rejection_reason'
      })
    }
  ];

  const formatTickets = (tickets) =>
    tickets.map((itm) => ({
      key: itm?._id,
      ticketId: itm?.externalId,
      status: (
        <BRTag
          color={
            TICKET_STATUS_TAG[itm?.status.toLowerCase().replaceAll(' ', '_')]
          }
        >
          {itm?.status}
        </BRTag>
      ),
      category: itm?.category,
      sub_category: itm?.subCategory || '-',
      investigation_result: itm?.investigationResult || '-',
      rejection_reason: itm?.rejectionReason || '-'
    }));

  const fetchFreshdeskTickets = async ({ page, limit }) => {
    if (!date) {
      setTicketsData({});
      return { list: [], total: 0 };
    }

    try {
      setIsLoading(true);
      const payload = {
        page: page || 1,
        // page: page - 1,
        limit,
        businessId,
        date: date?.format('YYYY-MM-DD')
      };
      const { data } = await getFreshDeskTickets(payload);
      setTicketsData(data);
      setIsLoading(false);

      return {
        list: formatTickets(data.tickets),
        total: data.totalNumberOfTickets
      };
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportClick = async () => {
    try {
      setIsLoading(true);
      const { data } = await exportFreshDeskTickets({
        businessId,
        date: date?.format('YYYY-MM-DD')
      });
      notify(data, 'success');
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="br-freshdesk-table">
      <BRTable
        title={
          <div className="br-freshdesk-table__title">
            <FreshDeskIcon />
            {intl.formatMessage(
              { id: 'merchant_analysis.freshdesk_table.title' },
              {
                count: ticketsData?.totalNumberOfTickets
              }
            )}
          </div>
        }
        preTableHeaderContent={
          !!ticketsData?.totalNumberOfTickets && (
            <>
              <Divider />
              <div className="br-freshdesk-table__tags">
                <StatsTags
                  ticketsData={ticketsData?.ticketsStatusCount || {}}
                  loading={isLoading}
                />
              </div>
            </>
          )
        }
        columns={starsTableColumns}
        listFunction={fetchFreshdeskTickets}
        showFilter={false}
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        shareMethods={acceptMethods}
        loading={isLoading}
        quickFilters={false}
        exportListFileFunction={
          isUserAuthorized([], [ACL_MATRIX.BUSINESS_TICKETS_EXPORT]) &&
          handleExportClick
        }
      />
    </div>
  );
};

export default FreshDeskTable;
